// Console auth endpoints — all on the console BFF origin (task 326.18, decision Q7).
//
// They used to point at the core API (VITE_AUTH_API_URL) while the rest of the app talked to the
// BFF. One origin means the refresh token can be an httpOnly cookie, which is the whole point:
// login sets it, refresh rotates it, logout clears it, and no JavaScript in this app ever sees it.

import { sessionApi } from '../session';
import type { ConsoleSession, MfaStep } from '../contracts/session';

export interface LoginRequest {
  username: string;
  password: string;
}

export interface SwitchTenantResponse {
  accessToken: string;
  tenantId: string;
  tenantCode: string;
  tenantName: string;
  /** Task 387.11.5: set, with an empty accessToken, when the target tenant needs verification again first. */
  mfa?: MfaStep | null;
}

export async function login(data: LoginRequest): Promise<ConsoleSession> {
  return sessionApi.post<ConsoleSession>('api/console/auth/login', data);
}

/** Revokes the session behind the cookie and clears it. Safe to call with an expired access token. */
export async function logout(): Promise<void> {
  return sessionApi.postVoid('api/console/auth/logout');
}

/**
 * Switches the active tenant of the current session. The session is identified by the cookie — the
 * old client sent a refresh token from localStorage here and then stored the response's absent
 * `refreshToken`, i.e. `undefined`, over the real one.
 *
 * The endpoint is `[Authorize]`: the cookie names the session, the bearer names the operator, and
 * the api answers 401 without either (task 335 — the cookie-only call after 326.18 could never
 * switch). The token is passed in, not read from the store, because the store imports this module.
 */
export async function switchTenant(
  data: { tenantId: string },
  bearer: string | null,
): Promise<SwitchTenantResponse> {
  return sessionApi.post<SwitchTenantResponse>('api/console/auth/switch-tenant', data, bearer);
}

/**
 * "Forgot password" (task 387.13). The api answers the same way whether or not the address has an account, so
 * this never tells the caller anything beyond "if it exists, a link was sent".
 */
export async function requestPasswordReset(email: string): Promise<void> {
  await sessionApi.postNoContent('api/console/auth/password/forgot', { email });
}

/** Sets a password with a single-use token from an emailed link or from signing in with a temporary password. */
export async function setPasswordWithToken(token: string, newPassword: string): Promise<void> {
  await sessionApi.postNoContent('api/console/auth/password/set', { token, newPassword });
}

/** Changes the signed-in operator's password. Every session ends, this one included. */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
  bearer: string | null,
): Promise<void> {
  await sessionApi.postNoContent(
    'api/console/auth/password/change',
    { currentPassword, newPassword },
    bearer,
  );
}

export type { ConsoleSession };
